/**
 * Task #3414 — Commissioning Agent AI: contratti FE↔BE del chat modal.
 *
 * SoT modulo: docs/incentivazione-analisi.md (§13). Il flusso proposta è a
 * due tempi: l'agente chiama il tool `propose_plan` (payload validato con
 * `commissioningAgentProposalSchema` e salvato in `pending_proposal`), il FE
 * mostra l'anteprima e la conferma dell'utente colpisce l'endpoint
 * DETERMINISTICO `apply-proposal` (zero LLM) che crea le entità in ordine,
 * forzando le gare in stato `draft` e risolvendo i riferimenti `ref:`.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Contesto hub (tab attivo + entità aperta) passato dal FE ad ogni messaggio.
// ---------------------------------------------------------------------------
export const COMMISSIONING_AGENT_TABS = [
  'gare',
  'configuratori',
  'functions',
  'value-packages',
  'variabili',
  'cluster',
] as const;
export type CommissioningAgentTab = (typeof COMMISSIONING_AGENT_TABS)[number];

export const COMMISSIONING_AGENT_ENTITY_KINDS = [
  'competition',
  'configurator_template',
  'configurator_instance',
  'function',
  'value_package',
  'variable',
  'reference_list',
  'cluster',
  'addon',
] as const;
export type CommissioningAgentEntityKind =
  (typeof COMMISSIONING_AGENT_ENTITY_KINDS)[number];

export const commissioningAgentContextSchema = z.object({
  tab: z.enum(COMMISSIONING_AGENT_TABS),
  entity: z
    .object({
      kind: z.enum(COMMISSIONING_AGENT_ENTITY_KINDS),
      id: z.string().uuid(),
      name: z.string().max(255).optional(),
    })
    .optional(),
});
export type CommissioningAgentContext = z.infer<typeof commissioningAgentContextSchema>;

// ---------------------------------------------------------------------------
// Scope conversazione: 'tenant' (default) oppure 'brand' (mirror Brand
// Interface con permessi invertiti — solo super_admin).
// ---------------------------------------------------------------------------
export const COMMISSIONING_AGENT_SCOPES = ['tenant', 'brand'] as const;
export type CommissioningAgentScope = (typeof COMMISSIONING_AGENT_SCOPES)[number];

export const createCommissioningAgentConversationSchema = z.object({
  scope: z.enum(COMMISSIONING_AGENT_SCOPES).optional().default('tenant'),
  context: commissioningAgentContextSchema.optional(),
});
export type CreateCommissioningAgentConversationInput = z.infer<
  typeof createCommissioningAgentConversationSchema
>;

export const commissioningAgentChatSchema = z.object({
  conversationId: z.string().uuid(),
  message: z.string().min(1).max(8000),
  context: commissioningAgentContextSchema.optional(),
});
export type CommissioningAgentChatInput = z.infer<typeof commissioningAgentChatSchema>;

// ---------------------------------------------------------------------------
// Proposta strutturata (tool `propose_plan`).
// ---------------------------------------------------------------------------
export const COMMISSIONING_PROPOSAL_ITEM_KINDS = [
  'variable_category',
  'variable',
  'reference_list',
  'function',
  'value_package',
  'configurator_template',
  'competition',
  'configurator_instance',
  'interaction',
  'cluster',
] as const;
export type CommissioningProposalItemKind =
  (typeof COMMISSIONING_PROPOSAL_ITEM_KINDS)[number];

/**
 * Handle intra-proposta: i campi uuid del payload possono valere
 * `"ref:<handle>"` per referenziare un item precedente della stessa proposta
 * (es. l'istanza referenzia la gara appena proposta). Risolto in apply.
 */
export const commissioningProposalRefSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/);

export const commissioningProposalItemSchema = z.object({
  kind: z.enum(COMMISSIONING_PROPOSAL_ITEM_KINDS),
  /** Handle riusabile dagli item successivi come "ref:<handle>". */
  ref: commissioningProposalRefSchema.optional(),
  /** Titolo leggibile mostrato nell'anteprima FE. */
  title: z.string().min(1).max(255),
  /** Nota di riuso: perché si crea nuovo invece di riusare dal catalogo. */
  reuse_note: z.string().max(2000).optional(),
  /** Body per il create schema condiviso dell'entità (validato in apply). */
  payload: z.record(z.unknown()),
});
export type CommissioningProposalItem = z.infer<typeof commissioningProposalItemSchema>;

export const commissioningAgentProposalSchema = z.object({
  summary: z.string().min(1).max(4000),
  items: z.array(commissioningProposalItemSchema).min(1).max(40),
  /** Avvertenza ricalcolo quando la proposta tocca gare ATTIVE. */
  recalc_note: z.string().max(2000).optional(),
});
export type CommissioningAgentProposal = z.infer<typeof commissioningAgentProposalSchema>;

export const applyCommissioningProposalSchema = z.object({
  conversationId: z.string().uuid(),
});
export type ApplyCommissioningProposalInput = z.infer<
  typeof applyCommissioningProposalSchema
>;

// ---------------------------------------------------------------------------
// Payload di risposta (tipizzati, condivisi col FE).
// ---------------------------------------------------------------------------
export interface CommissioningAgentChatResponse {
  message: string;
  proposal: CommissioningAgentProposal | null;
  model_used?: string;
  provider_used?: string;
}

export interface CommissioningProposalCreatedEntity {
  kind: CommissioningProposalItemKind;
  ref: string | null;
  id: string;
  name: string;
}

export interface CommissioningProposalApplyResult {
  created: CommissioningProposalCreatedEntity[];
  /** Avvisi di ricalcolo innescati (gare attive toccate dalla proposta). */
  recalc_warnings?: string[];
}

export interface CommissioningAgentConversationListItem {
  id: string;
  title: string | null;
  scope: CommissioningAgentScope;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface CommissioningAgentConversationDetail
  extends CommissioningAgentConversationListItem {
  messages: unknown;
  pending_proposal: CommissioningAgentProposal | null;
  context: CommissioningAgentContext | null;
}
